// Top-of-sidebar profile picker. Replaces the old vertical profile list.
// Click on the current profile to open a dropdown with: every profile,
// "Create new profile" CTA, and a logout entry. Matches the desktop's
// chat-row visual language so the dropdown rows feel native.
import { useEffect, useRef, useState } from "react";
import type { DesktopProfileSummary } from "@shared/api/schemas/profiles";
import { ApiError, httpClient } from "../../client/http-client";
import type { StoredSession } from "../../client/jwt-store";
import {
	LogoutIcon,
	PlusIcon,
	SignalLensLogo,
	UsersIcon,
} from "../../components/Icons";

interface ProfileSwitcherProps {
	profiles: DesktopProfileSummary[];
	selectedProfileId: string | null;
	session: StoredSession;
	onSelect: (profileId: string) => void;
	onProfilesChanged: () => void;
	onLogout: () => void;
	onOpenUserManagement?: () => void;
}

export const ProfileSwitcher = ({
	profiles,
	selectedProfileId,
	session,
	onSelect,
	onProfilesChanged,
	onLogout,
	onOpenUserManagement,
}: ProfileSwitcherProps) => {
	const [open, setOpen] = useState(false);
	const [creating, setCreating] = useState(false);
	const [newName, setNewName] = useState("");
	const [createError, setCreateError] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);
	const rootRef = useRef<HTMLDivElement | null>(null);

	const selected = profiles.find((p) => p.id === selectedProfileId) ?? null;

	useEffect(() => {
		if (!open) return;
		const handleClick = (event: MouseEvent) => {
			if (!rootRef.current) return;
			if (!rootRef.current.contains(event.target as Node)) {
				setOpen(false);
				setCreating(false);
				setCreateError(null);
				setNewName("");
			}
		};
		document.addEventListener("mousedown", handleClick);
		return () => document.removeEventListener("mousedown", handleClick);
	}, [open]);

	const handleCreate = async () => {
		const name = newName.trim();
		if (!name) {
			setCreateError("Give the profile a name.");
			return;
		}
		setBusy(true);
		setCreateError(null);
		try {
			await httpClient.profiles.create({ name });
			setNewName("");
			setCreating(false);
			setOpen(false);
			onProfilesChanged();
		} catch (err) {
			setCreateError(err instanceof ApiError ? err.message : String(err));
		} finally {
			setBusy(false);
		}
	};

	return (
		<div ref={rootRef} className="relative px-3 pt-3">
			<button
				type="button"
				onClick={() => setOpen((v) => !v)}
				className="tg-chat-row tg-text-primary flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition"
				aria-haspopup="listbox"
				aria-expanded={open}
			>
				<span className="tg-brand-mark inline-flex h-9 w-9 flex-none items-center justify-center rounded-full p-1">
					<SignalLensLogo className="h-full w-full" />
				</span>
				<span className="min-w-0 flex-1">
					<span className="block truncate text-[0.95rem] font-semibold leading-5">
						{selected?.name ?? "No profile"}
					</span>
					<span className="tg-text-muted block truncate text-[0.78rem] leading-5">
						{session.user.email}
					</span>
				</span>
				<span
					aria-hidden="true"
					className={`tg-text-muted transition-transform ${open ? "rotate-180" : ""}`}
				>
					▾
				</span>
			</button>

			{open ? (
				<div
					className="absolute left-3 right-3 top-[calc(100%+6px)] z-30 max-h-[70vh] overflow-y-auto rounded-2xl border border-[color:var(--color-tg-line)] bg-[color:var(--color-tg-card)] p-2 shadow-2xl"
					role="listbox"
				>
					<div className="px-2 pb-1 pt-1">
						<span className="tg-text-muted text-[0.7rem] font-semibold uppercase tracking-[0.14em]">
							Profiles
						</span>
					</div>

					{profiles.length === 0 ? (
						<p className="tg-text-muted px-3 py-3 text-[0.85rem] leading-6">
							No profiles yet — create one below.
						</p>
					) : (
						<ul className="flex flex-col gap-0.5">
							{profiles.map((profile) => {
								const isSelected = profile.id === selectedProfileId;
								return (
									<li key={profile.id}>
										<button
											type="button"
											onClick={() => {
												onSelect(profile.id);
												setOpen(false);
											}}
											className={[
												"tg-chat-row flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left transition",
												isSelected
													? "tg-chat-row-selected"
													: "tg-text-secondary",
											].join(" ")}
										>
											<span
												className={[
													"inline-flex h-8 w-8 flex-none items-center justify-center rounded-full text-[0.78rem] font-semibold",
													isSelected
														? "bg-[color:var(--color-tg-active-strong)] tg-avatar-text"
														: "tg-surface tg-text-secondary",
												].join(" ")}
											>
												{profile.name.slice(0, 2).toUpperCase()}
											</span>
											<span className="min-w-0 flex-1">
												<span
													className={[
														"block truncate text-[0.9rem] font-semibold leading-5",
														isSelected
															? "tg-text-on-active"
															: "tg-text-primary",
													].join(" ")}
												>
													{profile.name}
												</span>
												<span
													className={[
														"block truncate font-mono text-[0.72rem] leading-4",
														isSelected
															? "tg-text-on-active-muted"
															: "tg-text-muted",
													].join(" ")}
												>
													{profile.id.slice(0, 8)}
												</span>
											</span>
										</button>
									</li>
								);
							})}
						</ul>
					)}

					<div className="mt-2 border-t border-[color:var(--color-tg-line)] pt-2">
						{creating ? (
							<form
								className="grid gap-2 px-2 py-1"
								onSubmit={(event) => {
									event.preventDefault();
									void handleCreate();
								}}
							>
								<input
									className="tg-input tg-text-primary h-9 w-full rounded-lg px-3 text-[0.88rem]"
									autoFocus
									placeholder="Profile name"
									value={newName}
									onChange={(event) => setNewName(event.target.value)}
									disabled={busy}
								/>
								<div className="flex items-center justify-between gap-2">
									<button
										type="button"
										onClick={() => {
											setCreating(false);
											setCreateError(null);
											setNewName("");
										}}
										className="tg-btn-text text-[0.82rem] font-semibold"
										disabled={busy}
									>
										Cancel
									</button>
									<button
										type="submit"
										className="tg-btn-primary inline-flex h-8 items-center justify-center rounded-lg px-3 text-[0.82rem] font-semibold disabled:cursor-wait disabled:opacity-60"
										disabled={busy}
									>
										{busy ? "Creating…" : "Create"}
									</button>
								</div>
								{createError ? (
									<div
										className="tg-alert-error rounded-lg px-3 py-2 text-[0.8rem]"
										role="alert"
									>
										{createError}
									</div>
								) : null}
							</form>
						) : (
							<button
								type="button"
								onClick={() => setCreating(true)}
								className="tg-text-secondary flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-[0.86rem] font-semibold transition hover:bg-[color:var(--color-tg-hover)]"
							>
								<PlusIcon className="h-4 w-4" />
								<span>Create new profile</span>
							</button>
						)}

						{session.user.role === "admin" && onOpenUserManagement ? (
							<button
								type="button"
								onClick={() => {
									setOpen(false);
									onOpenUserManagement();
								}}
								className="tg-text-secondary flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-[0.86rem] font-semibold transition hover:bg-[color:var(--color-tg-hover)]"
							>
								<UsersIcon className="h-4 w-4" />
								<span>User management</span>
							</button>
						) : null}

						<button
							type="button"
							onClick={() => {
								setOpen(false);
								onLogout();
							}}
							className="tg-text-secondary flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-[0.86rem] font-semibold transition hover:bg-[color:var(--color-tg-hover)]"
						>
							<LogoutIcon className="h-4 w-4" />
							<span>Sign out ({session.user.role === "admin" ? "admin" : "user"})</span>
						</button>
					</div>
				</div>
			) : null}
		</div>
	);
};
